import { Localizable } from '@components/localizable/Localizable'
import { timezones } from '@components/utils/TimeZoneSelect'
import { Venue } from '@components/venue/Venue'
import { Plan } from '@models/membership/Plan'
import { useAppSelector } from '@store/hooks'
import { selectSelectedVenue } from '@store/VenueReducer'
import { SBAsyncSelect } from '@supplement-bacon/react-async-select'
import { SBRM, SBRMFormActionProps } from '@supplement-bacon/sbrm-react'
import { Entity } from '@utils/Entities'
import {
  confirmationRequiredRule,
  emailRule,
  requiredRule,
  soundcloudUrlRule,
  spotifyUrlRule,
  urlRule,
} from '@utils/rules'
import {
  Alert,
  Button,
  Card,
  Checkbox,
  Col,
  Divider,
  Form,
  Input,
  InputNumber,
  Radio,
  Row,
  Select,
  Space,
  Switch,
} from 'antd'
import { useEffect, useState } from 'react'
import { BookingConnectorEmailSection } from './sections/booking-connectors/Email'
import { BookingConnectorIntercomSection } from './sections/booking-connectors/Intercom'
import { BookingConnectorMembershipChatSection } from './sections/booking-connectors/MembershipChat'
import { BookingConnectorWhatsappSection } from './sections/booking-connectors/Whatsapp'
import { BookingLinkSection } from './sections/BookingLinkSection'
import { PhoneNumberSection } from './sections/PhoneNumberSection'

const Update = ({ form, formProps }: SBRMFormActionProps) => {
  const item = useAppSelector(selectSelectedVenue())
  const [hasChanged, setHasChanged] = useState(false)

  const [showAddressForm, setShowAddressForm] = useState<boolean>(true)
  const [viewMap, setViewMap] = useState<boolean>(false)

  const appVisibilityValue = Form.useWatch(['visibility', 'application'], form)
  const venueType = Form.useWatch('type', form)
  const whatsappVisibilityValue = Form.useWatch(
    ['visibility', 'whatsapp'],
    form
  )
  const appRestrictionValue = Form.useWatch(
    ['restriction', 'application'],
    form
  )
  const appRestrictionPlansValue = Form.useWatch(
    ['restriction', 'application_plans'],
    form
  )

  useEffect(() => {
    if (venueType !== undefined && venueType !== item?.type) {
      setHasChanged(true)
    }
  }, [venueType, item?.type])

  const showCheckbox = hasChanged && venueType !== 4

  useEffect(() => {
    if (!item) return
    form.setFieldsValue(item)
  }, [item])

  if (item === undefined) {
    return <></>
  }

  return (
    <Form {...formProps} form={form} layout="vertical">
      <Form.Item name="id" hidden style={{ display: 'none' }}>
        <Input type="hidden" />
      </Form.Item>
      <Form.Item name="slug" hidden style={{ display: 'none' }}>
        <Input type="hidden" />
      </Form.Item>
      <SBRM.FormSection section="main">
        <Form.Item label={'Name'} name="name" rules={[requiredRule()]}>
          <Input maxLength={45} showCount />
        </Form.Item>
        <Form.Item name="type" rules={[requiredRule()]} label={'Type'}>
          <SBAsyncSelect
            entity={Entity.venueType}
            searchFieldName="name"
            allowClear
          />
        </Form.Item>
        {showCheckbox && (
          <Form.Item
            name="venueAuthorization"
            valuePropName="checked"
            rules={[confirmationRequiredRule()]}
          >
            <Card size="small">
              <Checkbox>
                I acknowledge that adding a venue to the{' '}
                <b>Pléthore platform</b> may lead to an adjustment of the{' '}
                <b>annual fees</b>, and I confirm that I am duly
                <b> authorized </b> to make this addition.
              </Checkbox>
            </Card>
          </Form.Item>
        )}
      </SBRM.FormSection>
      <SBRM.FormSection section="geographic">
        {
          <>
            <Row gutter={[16, 16]}>
              <Col span={16}>
                <Form.Item
                  label={'Address'}
                  rules={[requiredRule()]}
                  name={['address', 'label']}
                >
                  <Input maxLength={45} showCount />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item
                  label={'Post code'}
                  rules={[requiredRule()]}
                  name={['address', 'postCode']}
                >
                  <Input />
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={[16, 16]}>
              <Col span={6}>
                <Form.Item
                  label={'Latitude'}
                  rules={[requiredRule()]}
                  name={['address', 'latitude']}
                >
                  <InputNumber
                    min={-90}
                    max={90}
                    required
                    className="w-full"
                    stringMode
                  />
                </Form.Item>
              </Col>
              <Col span={6}>
                <Form.Item
                  label={'Longitude'}
                  rules={[requiredRule()]}
                  name={['address', 'longitude']}
                >
                  <InputNumber
                    min={-180}
                    max={180}
                    required
                    className="w-full"
                    stringMode
                  />
                </Form.Item>
              </Col>
              <Col span={6}>
                <Form.Item
                  label={'Timezone'}
                  rules={[requiredRule()]}
                  name={'timezone'}
                >
                  <Select
                    options={timezones}
                    showSearch
                    placeholder="Sélectionnez un fuseau horaire"
                  />
                </Form.Item>
              </Col>
              <Col span={6}>
                <Button
                  onClick={() => {
                    setShowAddressForm(false)
                    setViewMap(true)
                  }}
                  block
                  style={{ marginTop: 30 }}
                >
                  Reset
                </Button>
              </Col>
            </Row>
          </>
        }

        {viewMap && (
          <Row gutter={[16, 16]}>
            <Col span={24}>
              <Venue.Map
                setShowAddressForm={setShowAddressForm}
                showAddressForm={showAddressForm}
              />
            </Col>
          </Row>
        )}
      </SBRM.FormSection>
      <SBRM.FormSection section="description">
        <Localizable.TextArea
          name={'descriptionBody'}
          label={'Description body'}
        />
      </SBRM.FormSection>
      <SBRM.FormSection section="schedule">
        <Localizable.Input
          name={'scheduleTitle'}
          label={'Schedule title'}
          max={70}
        />
        <Localizable.TextArea
          name={'scheduleBody'}
          label={'Schedule body'}
          max={255}
        />
      </SBRM.FormSection>
      <SBRM.FormSection section="map">
        <Localizable.Input name={'mapTitle'} label={'Map title'} max={70} />
        <Form.Item
          label={'Google Maps Link'}
          name={['address', 'googleMapsLink']}
          rules={[urlRule()]}
        >
          <Input maxLength={255} showCount />
        </Form.Item>
        <Form.Item
          label={'Apple Maps Link'}
          name={['address', 'appleMapsLink']}
          rules={[urlRule()]}
        >
          <Input maxLength={255} showCount />
        </Form.Item>
      </SBRM.FormSection>
      <SBRM.FormSection section="tags">
        <Form.Item name="tags" label={'Selected tags'}>
          <SBAsyncSelect
            entity={Entity.venueTag}
            searchFieldName="name"
            allowClear
            multiple
          />
        </Form.Item>
      </SBRM.FormSection>
      <SBRM.FormSection section="socials">
        <Form.Item label={'Instagram'} name="instagram" rules={[urlRule()]}>
          <Input maxLength={255} showCount />
        </Form.Item>
        <Form.Item label={'Facebook'} name="facebook" rules={[urlRule()]}>
          <Input maxLength={255} showCount />
        </Form.Item>
      </SBRM.FormSection>
      <SBRM.FormSection section="musics">
        <Localizable.Input name={'musicTitle'} label={'Music title'} max={70} />
        <Form.Item
          label={'Spotify playlist URL'}
          name="spotifyUrl"
          rules={[urlRule(), spotifyUrlRule()]}
          extra={
            'In the Spotify app or web player, open the playlist, tap the ⋯ menu, then Share → Copy link to playlist.'
          }
        >
          <Input maxLength={255} showCount />
        </Form.Item>
        <Form.Item
          label={'SoundCloud playlist URL'}
          name="soundcloudUrl"
          rules={[urlRule(), soundcloudUrlRule()]}
          extra={
            'On SoundCloud, open the playlist/set, click Share, then copy the URL from the Share tab.'
          }
        >
          <Input maxLength={255} showCount />
        </Form.Item>
      </SBRM.FormSection>
      <SBRM.FormSection section="app_visibility">
        <Form.Item label={'Visibility'} name={['visibility', 'application']}>
          <Radio.Group>
            <Radio.Button value="visible">Visible</Radio.Button>
            <Radio.Button value="hidden">Hidden</Radio.Button>
            <Radio.Button value="temporarilyClosed">
              Temporarily closed
            </Radio.Button>
          </Radio.Group>
        </Form.Item>
        {/* Rendered only when used, so the venue header language selector does
            not count a hidden translated field. */}
        {appVisibilityValue === 'temporarilyClosed' && (
          <Localizable.Input
            rules={[requiredRule()]}
            label={'Temporarily closed label'}
            name={'temporarilyClosed'}
          />
        )}
        {appVisibilityValue !== 'hidden' && (
          <Alert
            type="info"
            showIcon
            title="Link venue to cluster(s) to display it on app home."
            action={
              <Button
                color="primary"
                variant="outlined"
                href="/venues/clusters"
              >
                Cluster settings
              </Button>
            }
          />
        )}
        {appVisibilityValue !== 'hidden' && (
          <>
            <Divider />
            <Form.Item
              label={'Restriction'}
              name={['restriction', 'application']}
              initialValue={'everyone'}
            >
              <Radio.Group>
                <Radio.Button value="everyone">Everyone</Radio.Button>
                <Radio.Button value="only_members">Only members</Radio.Button>
                <Radio.Button value="only_non_members">
                  Only non-members
                </Radio.Button>
              </Radio.Group>
            </Form.Item>
            {appRestrictionValue === 'only_non_members' &&
              (appRestrictionPlansValue?.length ?? 0) > 0 && (
                <Alert
                  type="warning"
                  showIcon
                  title="Linked membership plans will be removed"
                  description="This venue has membership plans linked. Switching to 'Only non-members' applies to all non-members regardless of plan, so the linked plans will be removed on save."
                  style={{ marginBottom: 16 }}
                />
              )}
            {appRestrictionValue === 'only_members' && (
              <Form.Item
                name={['restriction', 'application_plans']}
                rules={[requiredRule()]}
              >
                <SBAsyncSelect<Plan>
                  entity={Entity.membershipPlan}
                  multiple
                  allowClear
                  placeholder="Plan selector"
                />
              </Form.Item>
            )}
          </>
        )}
      </SBRM.FormSection>
      <SBRM.FormSection section="whatsapp_visibility">
        <Form.Item label={'Visibility'} name={['visibility', 'whatsapp']}>
          <Radio.Group>
            <Radio.Button value="visible">Visible</Radio.Button>
            <Radio.Button value="hidden">Hidden</Radio.Button>
            <Radio.Button value="temporarilyClosed">
              Temporarily closed
            </Radio.Button>
          </Radio.Group>
        </Form.Item>
        {whatsappVisibilityValue === 'temporarilyClosed' && (
          <Localizable.Input
            rules={[requiredRule()]}
            label={'Temporarily closed label'}
            name={'whatsappTemporarilyClosed'}
          />
        )}
      </SBRM.FormSection>
      <SBRM.FormSection section="web_visibility">
        <Form.Item label={'Visibility'} name={['visibility', 'web']}>
          <Radio.Group>
            <Radio.Button value="visible">Visible</Radio.Button>
            <Radio.Button value="hidden">Hidden</Radio.Button>
            <Radio.Button value="temporarilyClosed">
              Temporarily closed
            </Radio.Button>
          </Radio.Group>
        </Form.Item>
      </SBRM.FormSection>
      <SBRM.FormSection section="booking-application">
        <Space orientation="horizontal">
          <Form.Item
            noStyle
            name={['booking', 'app', 'enable']}
            valuePropName="checked"
          >
            <Switch
              title="Enable booking"
              checkedChildren={'Enabled'}
              unCheckedChildren={'Disabled'}
            />
          </Form.Item>
          {/* NOT USED YET
          <Form.Item
            name={['booking', 'app', 'paymentLink']}
            valuePropName="checked"
            label="Generate payment link"
          >
            <Switch
              title="Enable booking"
              checkedChildren={'Enabled'}
              unCheckedChildren={'Disabled'}
            />
          </Form.Item> */}
        </Space>
      </SBRM.FormSection>
      <SBRM.FormSection section="booking-whatsapp">
        <Form.Item
          noStyle
          name={['booking', 'chatbot', 'enable']}
          valuePropName="checked"
          label="Enable booking"
        >
          <Switch
            title="Enable booking"
            checkedChildren={'Enabled'}
            unCheckedChildren={'Disabled'}
          />
        </Form.Item>
      </SBRM.FormSection>
      <SBRM.FormSection section="booking-company-back-office">
        <Form.Item
          noStyle
          name={['booking', 'companyBackOffice', 'enable']}
          valuePropName="checked"
          label="Enable booking"
        >
          <Switch
            title="Enable booking"
            checkedChildren={'Enabled'}
            unCheckedChildren={'Disabled'}
          />
        </Form.Item>
      </SBRM.FormSection>
      <SBRM.FormSection section="booking-external-api">
        <Form.Item
          noStyle
          name={['booking', 'externalBookingAPI', 'enable']}
          valuePropName="checked"
          label="Enable booking"
        >
          <Switch
            title="Enable booking"
            checkedChildren={'Enabled'}
            unCheckedChildren={'Disabled'}
          />
        </Form.Item>
      </SBRM.FormSection>
      <SBRM.FormSection section="booking-website">
        <Form.Item
          noStyle
          name={['booking', 'web', 'enable']}
          valuePropName="checked"
          label="Enable booking"
        >
          <Switch
            title="Enable booking"
            checkedChildren={'Enabled'}
            unCheckedChildren={'Disabled'}
          />
        </Form.Item>
      </SBRM.FormSection>
      <SBRM.FormSection section="booking-whatsapp-paymentLink">
        <Form.Item
          noStyle
          name={['booking', 'chatbot', 'paymentLink']}
          valuePropName="checked"
          label="Generate payment link"
        >
          <Switch
            title="Enable booking"
            checkedChildren={'Enabled'}
            unCheckedChildren={'Disabled'}
          />
        </Form.Item>
      </SBRM.FormSection>
      <SBRM.FormSection section="bookingEmail">
        <Form.Item name={'bookingEmail'} rules={[emailRule()]}>
          <Input />
        </Form.Item>
      </SBRM.FormSection>
      <SBRM.FormSection section="bookingLink">
        <BookingLinkSection />
      </SBRM.FormSection>
      <SBRM.FormSection section="phone">
        <PhoneNumberSection />
      </SBRM.FormSection>
      <SBRM.FormSection section="booking-connectors-whatsapp">
        <BookingConnectorWhatsappSection />
      </SBRM.FormSection>
      <SBRM.FormSection section="booking-connectors-intercom">
        <BookingConnectorIntercomSection />
      </SBRM.FormSection>
      <SBRM.FormSection section="booking-connectors-membership-chat">
        <BookingConnectorMembershipChatSection />
      </SBRM.FormSection>
      <SBRM.FormSection section="booking-connectors-email">
        <BookingConnectorEmailSection />
      </SBRM.FormSection>
      <SBRM.FormSection section="bookingNotification">
        <Localizable.TextEditor
          name={'bookingNotificationPlaced'}
          label={'Content'}
        />
      </SBRM.FormSection>
      <SBRM.FormSection section="reoptinLegal">
        <Localizable.TextEditor
          name={'reoptinLegal'}
          label={'Opt-in label'}
          rules={[requiredRule()]}
        />
      </SBRM.FormSection>
      <SBRM.FormSection section="reoptinMarketing">
        <Localizable.TextEditor
          name={'reoptinMarketing'}
          label={'Opt-in label'}
          rules={[requiredRule()]}
        />
      </SBRM.FormSection>
    </Form>
  )
}

export default Update
